import { useEffect, useMemo, useState } from "react";

import { Link, useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ArrowUpDown,
  Search,
  Upload,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import {
  useCostingPeriods,
  useInventoryCountByPlant,
  useInventoryVariance,
} from "~/hooks/costing";
import type {
  InventoryCountByPlantRow,
  InventoryVarianceRow,
} from "~/services/costing";
import { PLANT_LABELS, type Plant } from "~/types/purchasing";

import {
  PeriodSelector,
  fmtCLP,
  fmtQty,
  useCanEditCosting,
} from "./components/CosteoShared";
import { InventoryCountUploadDialog } from "./components/InventoryCountUploadDialog";

type SortKey =
  | "bucket"
  | "plant"
  | "code"
  | "name"
  | "quantity"
  | "unit_cost"
  | "total";
const NUMERIC_KEYS = new Set<SortKey>(["quantity", "unit_cost", "total"]);

type SortState = { key: SortKey; dir: "asc" | "desc" };

const cycleSort = (prev: SortState, key: SortKey): SortState =>
  prev.key === key
    ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
    : { key, dir: NUMERIC_KEYS.has(key) ? "desc" : "asc" };

const plantLabel = (p: string): string => PLANT_LABELS[p as Plant] ?? p;

interface SortHeaderProps {
  label: string;
  sortKey: SortKey;
  sort: SortState;
  onSort: (key: SortKey) => void;
  align?: "left" | "right";
  className?: string;
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  align = "left",
  className,
}: SortHeaderProps) {
  const active = sort.key === sortKey;
  const Icon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <TableHead
      onClick={() => onSort(sortKey)}
      className={`cursor-pointer select-none whitespace-nowrap hover:bg-muted/50 ${
        align === "right" ? "text-right" : ""
      } ${className ?? ""}`}
    >
      <span
        className={`inline-flex items-center gap-1 ${
          align === "right" ? "flex-row-reverse" : ""
        }`}
      >
        {label}
        <Icon
          className={`h-3.5 w-3.5 transition-transform duration-200 ${
            active ? "text-foreground" : "text-muted-foreground/40"
          }`}
        />
      </span>
    </TableHead>
  );
}

export function CosteoOpeningInventoryPage() {
  const navigate = useNavigate();
  const canEdit = useCanEditCosting();
  const { data: periods = [] } = useCostingPeriods();

  // Default to the most recent CLOSED month (that's the one being counted). Periods come sorted desc.
  const [period, setPeriod] = useState<string>("");
  useEffect(() => {
    if (!period && periods.length > 0) {
      const closed = periods.find((p) => p.status === "closed");
      setPeriod((closed ?? periods[0]).period);
    }
  }, [periods, period]);

  // The physical count is keyed by the calendar month-end date (e.g. 2026-07 → 2026-07-31).
  const countDate = useMemo(
    () => (period ? dayjs(`${period}-01`).endOf("month").format("YYYY-MM-DD") : ""),
    [period]
  );

  const { data: rows = [], isLoading } = useInventoryVariance(
    countDate || undefined
  );
  const { data: plantRows = [], isLoading: isLoadingPlant } =
    useInventoryCountByPlant(countDate || undefined);

  const [view, setView] = useState<"consolidado" | "por_planta">("consolidado");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortState>({ key: "total", dir: "desc" });
  const [plantSort, setPlantSort] = useState<SortState>({
    key: "bucket",
    dir: "asc",
  });
  const [uploadOpen, setUploadOpen] = useState(false);

  const handleSort = (key: SortKey) =>
    setSort((prev) => cycleSort(prev, key));
  const handlePlantSort = (key: SortKey) =>
    setPlantSort((prev) => cycleSort(prev, key));

  const total = useMemo(
    () => rows.reduce((s, r) => s + (r.real_valorizado ?? 0), 0),
    [rows]
  );

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term
      ? rows.filter(
          (r) =>
            (r.code ?? "").toLowerCase().includes(term) ||
            (r.name ?? "").toLowerCase().includes(term)
        )
      : rows;

    const val = (r: InventoryVarianceRow): number | string => {
      switch (sort.key) {
        case "bucket":
          return r.bucket ?? "";
        case "plant":
          return ""; // consolidated rows have no plant
        case "code":
          return r.code ?? "";
        case "name":
          return r.name ?? "";
        case "quantity":
          return r.real_qty ?? 0;
        case "unit_cost":
          return r.real_unit_cost ?? 0;
        case "total":
          return r.real_valorizado ?? 0;
      }
    };
    const mult = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      if (typeof va === "string" || typeof vb === "string") {
        return String(va).localeCompare(String(vb), "es") * mult;
      }
      return (va - vb) * mult;
    });
  }, [rows, search, sort]);

  const visiblePlantRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term
      ? plantRows.filter(
          (r) =>
            (r.code ?? "").toLowerCase().includes(term) ||
            (r.name ?? "").toLowerCase().includes(term)
        )
      : plantRows;

    const val = (r: InventoryCountByPlantRow): number | string => {
      switch (plantSort.key) {
        case "bucket":
          return r.bucket ?? "";
        case "plant":
          return r.plant ?? "";
        case "code":
          return r.code ?? "";
        case "name":
          return r.name ?? "";
        case "quantity":
          return r.real_qty ?? 0;
        case "unit_cost":
          return r.unit_cost ?? 0;
        case "total":
          return r.total_cost ?? 0;
      }
    };
    const mult = plantSort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      let cmp =
        typeof va === "string" || typeof vb === "string"
          ? String(va).localeCompare(String(vb), "es")
          : va - vb;
      cmp *= mult;
      if (cmp !== 0) return cmp;
      // Stable secondary ordering: bucket → plant → total desc.
      return (
        (a.bucket ?? "").localeCompare(b.bucket ?? "") ||
        (a.plant ?? "").localeCompare(b.plant ?? "") ||
        (b.total_cost ?? 0) - (a.total_cost ?? 0)
      );
    });
  }, [plantRows, search, plantSort]);

  return (
    <PageContainer>
      <PageHeader
        back={
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-muted-foreground"
            onClick={() => navigate("/costeo")}
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Volver
          </Button>
        }
        title="Inventario Final"
        description="Conteo físico del inventario al cierre de cada mes (Softland)"
        actions={
          <>
            <PeriodSelector periods={periods} value={period} onChange={setPeriod} />
            {canEdit && (
              <Button className="h-9" onClick={() => setUploadOpen(true)}>
                <Upload className="mr-2 h-4 w-4" />
                Cargar inventario
              </Button>
            )}
          </>
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-muted-foreground">
          Valorizado real:{" "}
          <span className="font-semibold text-foreground">{fmtCLP(total)}</span>
          {" · "}
          <Link
            to="/costeo/variacion-inventario"
            className="text-primary hover:underline"
          >
            Comparar con el sistema →
          </Link>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Tabs
            value={view}
            onValueChange={(v) => setView(v as "consolidado" | "por_planta")}
          >
            <TabsList className="grid w-full grid-cols-2 sm:w-auto">
              <TabsTrigger value="consolidado" className="h-9">
                Consolidado
              </TabsTrigger>
              <TabsTrigger value="por_planta" className="h-9">
                Por planta
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {(rows.length > 0 || plantRows.length > 0) && (
            <div className="relative sm:w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por código o ítem…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 pl-9"
              />
            </div>
          )}
        </div>
      </div>

      {view === "consolidado" && (
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <SortHeader
                  label="Bucket"
                  sortKey="bucket"
                  sort={sort}
                  onSort={handleSort}
                  className="w-24"
                />
                <SortHeader
                  label="Código"
                  sortKey="code"
                  sort={sort}
                  onSort={handleSort}
                  className="w-32"
                />
                <SortHeader
                  label="Ítem"
                  sortKey="name"
                  sort={sort}
                  onSort={handleSort}
                />
                <SortHeader
                  label="Cantidad"
                  sortKey="quantity"
                  sort={sort}
                  onSort={handleSort}
                  align="right"
                />
                <SortHeader
                  label="Costo Unit."
                  sortKey="unit_cost"
                  sort={sort}
                  onSort={handleSort}
                  align="right"
                />
                <SortHeader
                  label="Total"
                  sortKey="total"
                  sort={sort}
                  onSort={handleSort}
                  align="right"
                />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={6}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="py-10 text-center text-muted-foreground"
                  >
                    Sin conteo cargado para {period || "este período"}.
                    {canEdit &&
                      ' Use "Cargar inventario" para subir el archivo de Softland.'}
                  </TableCell>
                </TableRow>
              ) : visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="py-8 text-center text-muted-foreground"
                  >
                    No se encontraron ítems para «{search}»
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((row, index) => (
                  <TableRow
                    key={`${row.code}-${index}`}
                    className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                    style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
                  >
                    <TableCell>
                      <Badge variant="outline">{row.bucket ?? "—"}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{row.code}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell className="text-right">
                      {fmtQty(row.real_qty)}
                    </TableCell>
                    <TableCell className="text-right">
                      {fmtCLP(row.real_unit_cost)}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {fmtCLP(row.real_valorizado)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      )}

      {view === "por_planta" && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortHeader
                    label="Planta"
                    sortKey="plant"
                    sort={plantSort}
                    onSort={handlePlantSort}
                    className="w-28"
                  />
                  <SortHeader
                    label="Bucket"
                    sortKey="bucket"
                    sort={plantSort}
                    onSort={handlePlantSort}
                    className="w-24"
                  />
                  <SortHeader
                    label="Código"
                    sortKey="code"
                    sort={plantSort}
                    onSort={handlePlantSort}
                    className="w-32"
                  />
                  <SortHeader
                    label="Ítem"
                    sortKey="name"
                    sort={plantSort}
                    onSort={handlePlantSort}
                  />
                  <SortHeader
                    label="Cantidad"
                    sortKey="quantity"
                    sort={plantSort}
                    onSort={handlePlantSort}
                    align="right"
                  />
                  <SortHeader
                    label="Costo Unit."
                    sortKey="unit_cost"
                    sort={plantSort}
                    onSort={handlePlantSort}
                    align="right"
                  />
                  <SortHeader
                    label="Total"
                    sortKey="total"
                    sort={plantSort}
                    onSort={handlePlantSort}
                    align="right"
                  />
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingPlant ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={7}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : plantRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="py-10 text-center text-muted-foreground"
                    >
                      Este conteo se cargó consolidado (sin división por planta).
                    </TableCell>
                  </TableRow>
                ) : visiblePlantRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="py-8 text-center text-muted-foreground"
                    >
                      No se encontraron ítems para «{search}»
                    </TableCell>
                  </TableRow>
                ) : (
                  visiblePlantRows.map((row, index) => (
                    <TableRow
                      key={`${row.plant}-${row.code}-${index}`}
                      className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                      style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
                    >
                      <TableCell>
                        <Badge variant="outline">{plantLabel(row.plant)}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{row.bucket ?? "—"}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {row.code}
                      </TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell className="text-right">
                        {fmtQty(row.real_qty)}
                      </TableCell>
                      <TableCell className="text-right">
                        {fmtCLP(row.unit_cost)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {fmtCLP(row.total_cost)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {countDate && (
        <InventoryCountUploadDialog
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          countDate={countDate}
          monthLabel={period}
        />
      )}
    </PageContainer>
  );
}
